import * as React from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import { ResourceHeader } from '@factorialco/f0-react/dist/experimental'
import { ExternalLink } from '@factorialco/f0-react/icons/app'
import {
  JoinPostsGroupDocument,
  MyCommunitiesDocument,
  OtherCommunitiesDocument,
  PostsGroupHeaderDocument,
} from 'generated/resources/graphql'

import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { CommunitiesDropdown } from 'modules/dashboard/components/Groups/types'
import CommunityPostList from 'modules/engagement/components/CommunityPostList'
import CommunitiesSidebar from 'modules/posts/components/CommunitiesSidebar'
import { MembersList } from 'modules/posts/components/Groups/CommunityHeader/MembersList'
import CommunityQuickPostEditor from 'modules/posts/components/QuickPostEditor/CommunityQuickPostEditor'
import { buildAvatarUrl } from 'modules/posts/lib/buildAvatarUrl'
import { buildCommunityHeaderProps } from 'modules/posts/routes/ResourceHeaders/community_header'

const ShowCommunityPage = () => {
  const { id } = useParams()
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const [isMembersModalOpen, setIsMembersModalOpen] = React.useState(false)

  const { data, refetch } = useQuery(PostsGroupHeaderDocument, {
    variables: {
      id: id ? Number(id) : 0,
    },
    skip: !id,
  })

  const group = data?.posts.group
  const groupTitle = group?.title

  const [joinGroup] = useMutation(JoinPostsGroupDocument, {
    extractorFn: (mutationData) => mutationData.postsMutations.joinPostsGroup,
  })

  const selectedCommunity: CommunitiesDropdown = React.useMemo(() => {
    if (!id) {
      return {
        title: '',
        strategy: 'all',
      }
    }
    return {
      title: groupTitle || '',
      strategy: 'group',
      value: Number(id),
    }
  }, [id, groupTitle])

  const headerProps = React.useMemo(() => {
    if (!group) return null
    return buildCommunityHeaderProps({ group })
  }, [group])

  const metadataWithActions = React.useMemo(() => {
    if (!group || !group.employees.length) return undefined

    const allMembersAvatars = group.employees.map((employee) => ({
      firstName: employee.firstName ?? '',
      lastName: employee.lastName ?? '',
      type: 'person' as const,
      src: buildAvatarUrl(employee.avatar),
    }))

    return [
      {
        label: i18n.t('posts.communities.members'),
        value: {
          type: 'list' as const,
          variant: 'person' as const,
          avatars: allMembersAvatars,
        },
        actions: [
          {
            label: i18n.t('posts.communities.members'),
            icon: ExternalLink,
            onClick: () => setIsMembersModalOpen(true),
          },
        ],
      },
    ]
  }, [group])

  const handleActionClick = async (key: string): Promise<void> => {
    if (!id) return

    switch (key) {
      case 'set-automatic-invites':
        // TODO: Implement set automatic invites functionality
        break
      case 'join-group': {
        const joinResult = await joinGroup({
          variables: {
            id: Number(id),
          },
          refetchQueries: [
            {
              query: MyCommunitiesDocument,
            },
            {
              query: OtherCommunitiesDocument,
            },
          ],
          awaitRefetchQueries: true,
        })
        const handled = handleResult(joinResult, (_errors) => {
          app.setFlash({
            type: 'wrong',
            message: i18n.t('posts.communities.error_message'),
          })
        })
        if (handled.success) {
          app.setFlash({
            type: 'correct',
            message: i18n.t('dashboard.groups.join.submit'),
          })
          await refetch()
        }
        break
      }
      case 'edit-community':
        navigate(navigation.dashboard.groups.id.edit.fullPath({ id }))
        break
      case 'invite-people':
      case 'add-members':
        navigate(navigation.dashboard.groups.id.addMembers.fullPath({ id }))
        break
      case 'remove-members':
        navigate(navigation.dashboard.groups.id.removeMembers.fullPath({ id }))
        break
      case 'leave-group':
        navigate(navigation.dashboard.groups.id.leave.fullPath({ id }))
        break
      case 'delete-community':
        navigate(navigation.dashboard.groups.id.delete.fullPath({ id }))
        break
    }
  }

  const rightContentRef = React.useRef<HTMLDivElement>(null)

  return (
    <Box
      width="full"
      flexDirection="row"
      style={{
        height: '100%',
        overflow: 'hidden',
        display: 'flex',
      }}
    >
      <CommunitiesSidebar />
      <Box
        ref={rightContentRef}
        width="full"
        flexGrow
        style={{ height: '100%', overflow: 'hidden', display: 'flex' }}
        flexDirection="column"
      >
        {headerProps && (
          <Box paddingX="s24" paddingY="s16" flexNoShrink>
            <ResourceHeader
              {...headerProps}
              metadata={metadataWithActions}
              primaryAction={
                headerProps.primaryAction
                  ? headerProps.primaryAction.items
                    ? {
                        ...headerProps.primaryAction,
                        onClick: (value: string) => handleActionClick(value),
                      }
                    : {
                        ...headerProps.primaryAction,
                        onClick: () => handleActionClick(headerProps.primaryAction?.key || ''),
                      }
                  : undefined
              }
              secondaryActions={
                headerProps.secondaryActions
                  ? headerProps.secondaryActions.map((action) => ({
                      ...action,
                      onClick: () => handleActionClick(action.key),
                    }))
                  : undefined
              }
              otherActions={
                headerProps.otherActions
                  ? headerProps.otherActions.map((action) => ({
                      ...action,
                      onClick: () => handleActionClick(action.key),
                    }))
                  : undefined
              }
            />
          </Box>
        )}
        {group && id && (
          <MembersList
            groupId={Number(id)}
            groupTitle={group.title ?? ''}
            initialAvatars={group.employees.map((employee) => ({
              firstName: employee.firstName ?? '',
              lastName: employee.lastName ?? '',
              type: 'person' as const,
              src: buildAvatarUrl(employee.avatar),
            }))}
            isOpen={isMembersModalOpen}
            onOpenChange={setIsMembersModalOpen}
            showTrigger={false}
          />
        )}
        <Box
          flexGrow
          style={{ overflowY: 'auto', overflowX: 'hidden', minHeight: '0' }}
          paddingX="s24"
          paddingBottom="s24"
        >
          <Box maxWidth="s600" width="full" alignSelf="center">
            {group && group._policies?.addPosts?.can && id && (
              <Box padding="s16">
                <CommunityQuickPostEditor groupId={Number(id)} />
              </Box>
            )}
            <CommunityPostList selectedCommunity={selectedCommunity} />
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

export default ShowCommunityPage
